import { useEffect, useState } from 'react'
import dayjs from 'dayjs'

import { getMonthlyBillsOverview } from '@/service/bills'

type MonthlyBillingData = {
  incomeAmount: number
  expenditureAmount: number
  status: string
  yearMonth: number
}

type UseMonthlyBillingDataReturn = {
  data: MonthlyBillingData | null
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

/**
 * 获取当月收支数据的自定义 hook
 * @param userId 用户ID，如果不传则获取所有用户数据
 * @returns 当月收支数据、加载状态、错误信息和刷新函数
 */
export function useMonthlyBillingData(
  userId?: string,
): UseMonthlyBillingDataReturn {
  const [data, setData] = useState<MonthlyBillingData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchData = async () => {
    try {
      setLoading(true)
      setError(null)

      const currentMonth = dayjs().format('YYYYMM')
      const params: any = {
        yearMonthStart: currentMonth,
        yearMonthEnd: currentMonth,
      }

      if (userId) {
        params.userId = userId
        params.allUser = false
      } else {
        params.allUser = true
      }

      const response = await getMonthlyBillsOverview(params)

      if (response?.results && response.results.length > 0) {
        setData(response.results[0])
      } else {
        // 如果没有数据，设置默认值
        setData({
          incomeAmount: 0,
          expenditureAmount: 0,
          status: 'pending',
          yearMonth: parseInt(currentMonth),
        })
      }
    } catch (err) {
      console.error('Failed to fetch monthly billing data:', err)
      setError(err instanceof Error ? err.message : '获取数据失败')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId])

  return {
    data,
    loading,
    error,
    refetch: fetchData,
  }
}
